import type { ChallengeDefinition } from "../domain/schema";
import { shuffleAnswerPresentation } from "./presentationOrder";

interface ChallengeControllerOptions {
  panel: HTMLElement;
  onComplete(challenge: ChallengeDefinition, hintsUsed: number): void;
  onClose(): void;
  speak(text: string): void;
}

export class SkeletonChallengeController {
  private challenge?: ChallengeDefinition;
  private hintsUsed = 0;
  private selectedBone?: string;
  private placedBones = new Set<string>();
  private step = 0;
  private sequence: string[] = [];

  constructor(private readonly options: ChallengeControllerOptions) {}

  open(challenge: ChallengeDefinition): void {
    this.challenge = challenge;
    this.hintsUsed = 0;
    this.selectedBone = undefined;
    this.placedBones = new Set();
    this.step = 0;
    this.sequence = [];
    this.options.panel.hidden = false;
    this.render();
    shuffleAnswerPresentation(this.options.panel);
    this.options.panel
      .querySelector<HTMLButtonElement>("[data-action='close']")
      ?.focus();
  }

  close(): void {
    this.options.panel.hidden = true;
    this.options.panel.innerHTML = "";
    this.options.onClose();
  }

  private render(): void {
    const challenge = this.challenge;
    if (!challenge) return;
    this.options.panel.innerHTML = `
      <div class="challenge-heading">
        <div>
          <span class="pill">Skeleton Safari field station</span>
          <h2 id="challenge-title">${challenge.title}</h2>
        </div>
        <button class="icon-button" data-action="close" type="button" aria-label="Return to the trail">×</button>
      </div>
      <div id="challenge-body" class="challenge-body"></div>
      <div class="hint-row">
        <button class="secondary-button secondary-button--compact" data-action="hint" type="button">Need a hint?</button>
        <p id="hint-text" class="hint-text" aria-live="polite">Try it your way first. You can always ask for help.</p>
      </div>
      <output id="challenge-feedback" class="challenge-feedback" aria-live="polite"></output>
    `;
    this.options.panel
      .querySelector<HTMLButtonElement>("[data-action='close']")
      ?.addEventListener("click", () => this.close());
    this.options.panel
      .querySelector<HTMLButtonElement>("[data-action='hint']")
      ?.addEventListener("click", () => this.showHint());

    if (challenge.kind === "assembly") this.renderAssembly();
    else if (challenge.kind === "matching") this.renderBoneMatching();
    else if (challenge.kind === "comparison") this.renderComparison();
    else this.renderScenario();
  }

  private body(): HTMLElement {
    const body =
      this.options.panel.querySelector<HTMLElement>("#challenge-body");
    if (!body) throw new Error("Challenge body is missing");
    return body;
  }

  private feedback(message: string, correct = false): void {
    const output = this.options.panel.querySelector<HTMLOutputElement>(
      "#challenge-feedback",
    );
    if (!output) return;
    output.textContent = message;
    output.classList.toggle("challenge-feedback--success", correct);
    if (correct || this.hintsUsed > 0) this.options.speak(message);
  }

  private clearFeedback(): void {
    const output = this.options.panel.querySelector<HTMLOutputElement>(
      "#challenge-feedback",
    );
    if (!output) return;
    output.textContent = "";
    output.classList.remove("challenge-feedback--success");
  }

  private showHint(): void {
    const challenge = this.challenge;
    if (!challenge) return;
    this.hintsUsed = Math.min(2, this.hintsUsed + 1);
    const hint = challenge.hints[this.hintsUsed - 1] ?? challenge.hints[1];
    const hintText =
      this.options.panel.querySelector<HTMLElement>("#hint-text");
    if (hintText) hintText.textContent = hint;
    this.options.speak(hint);
    const button = this.options.panel.querySelector<HTMLButtonElement>(
      "[data-action='hint']",
    );
    if (button) {
      button.textContent =
        this.hintsUsed === 1 ? "Show a bigger hint" : "Hint shown";
      button.disabled = this.hintsUsed >= 2;
    }
  }

  private finish(): void {
    const challenge = this.challenge;
    if (!challenge) return;
    this.feedback(challenge.successExplanation, true);
    const body = this.body();
    body.innerHTML += `<button class="primary-button finish-button" data-action="finish" type="button">Restore this trail clue</button>`;
    body
      .querySelector<HTMLButtonElement>("[data-action='finish']")
      ?.addEventListener("click", () => {
        this.options.onComplete(challenge, this.hintsUsed);
        this.close();
      });
  }

  private renderAssembly(): void {
    const bones = [
      ["skull", "Skull"],
      ["jaw", "Jaw"],
      ["rib-cage", "Rib cage"],
      ["spine", "Spine"],
      ["hip", "Hip"],
      ["upper-arm", "Upper arm"],
      ["lower-arm", "Lower arm"],
      ["upper-leg", "Upper leg"],
      ["lower-leg", "Lower leg"],
    ] as const;
    const slots = [
      ["skull", "Head frame"],
      ["jaw", "Moving bone below the skull"],
      ["rib-cage", "Curved chest bones"],
      ["spine", "Backbone"],
      ["hip", "Joins the legs to the body"],
      ["upper-arm", "Shoulder to elbow"],
      ["lower-arm", "Elbow to hand"],
      ["upper-leg", "Hip to knee"],
      ["lower-leg", "Knee to foot"],
    ] as const;

    this.body().innerHTML = `
      <p>Build the human skeleton model used at the field station. Select a bone, then place it on the matching body area. Notice how every bone has a useful size and shape.</p>
      <div class="bone-workbench">
        <div class="bone-tray" aria-label="Model bones">
          ${bones.map(([id, label]) => `<button type="button" data-bone="${id}">${boneIcon(id)}<span>${label}</span></button>`).join("")}
        </div>
        <div class="skeleton-board" aria-label="Skeleton model slots">
          <div class="skeleton-silhouette" aria-hidden="true">${humanSkeletonSvg()}</div>
          <div class="bone-slots">
            ${slots.map(([answer, clue]) => `<button type="button" data-slot="${answer}" aria-label="Place bone: ${clue}"><span>${clue}</span><strong>?</strong></button>`).join("")}
          </div>
        </div>
      </div>
    `;

    for (const button of this.options.panel.querySelectorAll<HTMLButtonElement>(
      "[data-bone]",
    )) {
      button.addEventListener("click", () => {
        if (button.disabled) return;
        this.selectedBone = button.dataset.bone;
        for (const peer of this.options.panel.querySelectorAll("[data-bone]")) {
          peer.classList.toggle("selected", peer === button);
        }
        this.feedback(
          `${button.textContent?.trim()} selected. Now choose its place.`,
        );
      });
    }

    for (const slot of this.options.panel.querySelectorAll<HTMLButtonElement>(
      "[data-slot]",
    )) {
      slot.addEventListener("click", () => {
        if (!this.selectedBone) {
          this.feedback(
            "Choose a model bone from the tray first. Nothing is lost—take your time.",
          );
          return;
        }
        if (slot.dataset.slot !== this.selectedBone) {
          this.feedback(
            "That shape belongs somewhere else. Look at the clue and try another place.",
          );
          return;
        }
        const source = this.options.panel.querySelector<HTMLButtonElement>(
          `[data-bone='${this.selectedBone}']`,
        );
        const label = source?.textContent?.trim() ?? this.selectedBone;
        slot.innerHTML = `<span>${slot.querySelector("span")?.textContent ?? ""}</span><strong>${label}</strong>`;
        slot.classList.add("placed");
        slot.disabled = true;
        if (source) {
          source.disabled = true;
          source.classList.remove("selected");
        }
        this.placedBones.add(this.selectedBone);
        this.selectedBone = undefined;
        this.feedback(`${label} fits!`, true);
        if (this.placedBones.size === bones.length) this.finish();
      });
    }
  }

  private renderScenario(): void {
    this.clearFeedback();
    const isGuardian = this.challenge?.id === "safari.guardian-grove";
    const questions = isGuardian
      ? [
          {
            prompt:
              "Your explorer bumps their head on a low branch. Which bone protects the brain?",
            choices: ["Rib cage", "Skull", "Hip"],
            answer: "Skull",
            explanation: "The skull forms a hard case around the brain.",
          },
          {
            prompt:
              "Which bones protect the heart and lungs while your explorer runs?",
            choices: ["Rib cage", "Jaw", "Lower leg bones"],
            answer: "Rib cage",
            explanation: "The curved rib cage surrounds the heart and lungs.",
          },
        ]
      : [
          {
            prompt:
              "Why does your explorer's skeleton help their body keep its human shape?",
            choices: [
              "It is a firm supporting frame",
              "Bones are full of air",
              "It makes her fur grow",
            ],
            answer: "It is a firm supporting frame",
            explanation:
              "The firm skeleton supports the body and gives it shape.",
          },
          {
            prompt: "How do your explorer's leg bones help them jump?",
            choices: [
              "They move by themselves",
              "Muscles pull on them",
              "They turn soft",
            ],
            answer: "Muscles pull on them",
            explanation: "Muscles pull on bones to create movement.",
          },
        ];
    const question = questions[this.step];
    if (!question) {
      this.finish();
      return;
    }
    this.body().innerHTML = `
      <div class="scenario-illustration ${isGuardian ? "scenario-illustration--shield" : "scenario-illustration--bridge"}" aria-hidden="true"><span>${isGuardian ? "♡" : "↟"}</span></div>
      <p class="challenge-question">${question.prompt}</p>
      <div class="choice-grid">
        ${question.choices.map((choice) => `<button type="button" data-choice="${choice}">${choice}</button>`).join("")}
      </div>
    `;
    shuffleAnswerPresentation(this.body());
    for (const button of this.options.panel.querySelectorAll<HTMLButtonElement>(
      "[data-choice]",
    )) {
      button.addEventListener("click", () => {
        if (button.dataset.choice !== question.answer) {
          this.feedback(
            "Not this time—but you can try another idea. Think about what the skeleton does.",
          );
          button.classList.add("try-again");
          return;
        }
        this.feedback(question.explanation, true);
        this.step += 1;
        window.setTimeout(() => this.renderScenario(), 550);
      });
    }
  }

  private renderComparison(): void {
    this.clearFeedback();
    if (this.step === 0) {
      const stages = [
        ["young", "Young child"],
        ["growing", "Growing teenager"],
        ["adult", "Adult"],
      ] as const;
      this.body().innerHTML = `
        <p>Choose the people from youngest to oldest. A child's skeleton grows as their body grows; an adult's skeleton has stopped growing.</p>
        <div class="growth-sequence" aria-label="Growth order">
          ${stages
            .map(
              ([id, label]) =>
                `<button type="button" data-stage="${id}"><span class="person-size person-size--${id}" aria-hidden="true">●</span>${label}</button>`,
            )
            .reverse()
            .join("")}
        </div>
        <p class="sequence-readout">Your order: <strong id="sequence-value">none yet</strong></p>
      `;
      shuffleAnswerPresentation(this.body());
      for (const button of this.options.panel.querySelectorAll<HTMLButtonElement>(
        "[data-stage]",
      )) {
        button.addEventListener("click", () => {
          const value = button.dataset.stage;
          if (!value || this.sequence.includes(value)) return;
          this.sequence.push(value);
          button.disabled = true;
          const labels: Record<string, string> = {
            young: "young child",
            growing: "growing teenager",
            adult: "adult",
          };
          const readout = this.options.panel.querySelector("#sequence-value");
          if (readout) {
            readout.textContent = this.sequence
              .map((id) => labels[id])
              .join(" → ");
          }
          if (this.sequence.length === 3) {
            if (this.sequence.join(",") === "young,growing,adult") {
              this.feedback(
                "Yes. A child's skeleton grows as the child grows. An adult's skeleton has stopped growing.",
                true,
              );
              this.step = 1;
              window.setTimeout(() => this.renderComparison(), 650);
            } else {
              this.feedback(
                "Those stages are out of order. No problem—start with the youngest child.",
              );
              window.setTimeout(() => {
                this.sequence = [];
                this.renderComparison();
              }, 750);
            }
          }
        });
      }
      return;
    }

    if (this.step === 1) {
      this.body().innerHTML = `
        <p>Look closely at what the human skeleton model can show—and what it leaves out.</p>
        <div class="model-compare" aria-label="Comparison of a living human body and a card human skeleton model">
          <article class="model-card model-card--living">
            <div class="model-visual" aria-hidden="true">${livingHumanDiagram()}</div>
            <h3>Living human body</h3>
            <ul>
              <li>Bones are joined inside a living body</li>
              <li>Has muscles, joints, organs, and other tissues</li>
              <li>A child's skeleton grows as the child grows</li>
            </ul>
          </article>
          <article class="model-card model-card--card">
            <div class="model-visual" aria-hidden="true">${cardHumanModelDiagram()}</div>
            <h3>Card human skeleton model</h3>
            <ul>
              <li>Shows simplified bone shapes and positions</li>
              <li>Uses card and fasteners, not living tissues</li>
              <li>Cannot grow by itself</li>
            </ul>
          </article>
        </div>
        <p class="same-banner"><strong>Same:</strong> both help us see where the main human bones are arranged.</p>
        <p class="challenge-question">Which answer explains both what is useful about the model and how it differs from a real skeleton?</p>
        <div class="choice-grid">
          <button type="button" data-model-answer="wrong">They are exactly the same in every way</button>
          <button type="button" data-model-answer="correct">Both show bone positions, but the card model leaves out living tissues and cannot grow</button>
          <button type="button" data-model-answer="wrong">Only the card model has bones and joints</button>
        </div>
      `;
      shuffleAnswerPresentation(this.body());
      for (const button of this.options.panel.querySelectorAll<HTMLButtonElement>(
        "[data-model-answer]",
      )) {
        button.addEventListener("click", () => {
          if (button.dataset.modelAnswer !== "correct") {
            this.feedback(
              "A model is useful, but it is simplified. Try the answer that explains a limitation.",
            );
            return;
          }
          this.feedback(
            "Exactly. The model shows the main bone arrangement, but it is not a complete living skeleton.",
            true,
          );
          this.step = 2;
          window.setTimeout(() => this.renderComparison(), 700);
        });
      }
      return;
    }

    this.body().innerHTML = `
      <div class="transfer-check">
        <div class="transfer-rabbit" aria-hidden="true">${rabbitSkeletonDiagram()}</div>
        <div>
          <span class="pill">Use the same idea with an animal</span>
          <p class="challenge-question">Fern has a different body shape, but which statement is still true?</p>
          <div class="choice-grid choice-grid--stacked">
            <button type="button" data-transfer-answer="wrong">Only people have skeletons that support their bodies</button>
            <button type="button" data-transfer-answer="correct">Fern's skull protects her brain and her skeleton supports her body</button>
            <button type="button" data-transfer-answer="wrong">A rabbit skeleton is made from card and fasteners</button>
          </div>
        </div>
      </div>
    `;
    shuffleAnswerPresentation(this.body());
    for (const button of this.options.panel.querySelectorAll<HTMLButtonElement>(
      "[data-transfer-answer]",
    )) {
      button.addEventListener("click", () => {
        if (button.dataset.transferAnswer !== "correct") {
          this.feedback(
            "People and many animals both have living skeletons. Try the statement that applies the human-skeleton idea to Fern.",
          );
          return;
        }
        this.finish();
      });
    }
  }

  private renderBoneMatching(): void {
    const bones = ["Skull", "Rib cage", "Spine", "Hip"] as const;
    const matches = [
      ["head", "Which bone is found in the head?", "Skull"],
      ["chest", "Which bones curve around the chest?", "Rib cage"],
      ["back", "Which joined bones run down the back?", "Spine"],
      ["pelvis", "Which bone area joins the legs to the body?", "Hip"],
      ["brain", "Which bone protects the brain?", "Skull"],
      ["heart-lungs", "Which bones protect the heart and lungs?", "Rib cage"],
      [
        "support",
        "Which bones form the body's central supporting column?",
        "Spine",
      ],
      [
        "leg-joint",
        "Which bone area supports where the upper legs join?",
        "Hip",
      ],
    ] as const;
    const choices = `<option value="">Choose a bone</option>${bones
      .map((bone) => `<option value="${bone}">${bone}</option>`)
      .join("")}`;
    this.body().innerHTML = `
      <p><strong>Match bones to where they are and what they do.</strong> Choose one bone for every card, then use the purple <strong>Submit answers</strong> button.</p>
      <div class="bone-match-workbench">
        <div class="bone-match-model" aria-label="Human skeleton reference">${humanSkeletonSvg()}</div>
        <form id="bone-match-form">
          <div class="bone-match-grid">
            ${matches
              .map(
                ([id, prompt]) =>
                  `<label class="bone-match-card" data-match-card="${id}"><span>${prompt}</span><select data-bone-match="${id}" aria-label="${prompt}">${choices}</select></label>`,
              )
              .join("")}
          </div>
          <button class="primary-button submit-answers" type="submit">Submit answers</button>
        </form>
      </div>
    `;
    this.options.panel
      .querySelector<HTMLFormElement>("#bone-match-form")
      ?.addEventListener("submit", (event) => {
        event.preventDefault();
        let firstWrong: string | undefined;
        for (const [id, prompt, answer] of matches) {
          const select = this.options.panel.querySelector<HTMLSelectElement>(
            `[data-bone-match='${id}']`,
          );
          const card = this.options.panel.querySelector<HTMLElement>(
            `[data-match-card='${id}']`,
          );
          const correct = select?.value === answer;
          card?.classList.toggle("bone-match-card--correct", correct);
          card?.classList.toggle("bone-match-card--wrong", !correct);
          if (!correct && !firstWrong) firstWrong = prompt;
        }
        if (firstWrong) {
          this.feedback(
            `Check “${firstWrong}” and try again. Use the body position and protective or supporting job as your clue.`,
          );
          return;
        }
        this.finish();
      });
  }
}

function boneIcon(id: string): string {
  if (id === "skull") return "◉";
  if (id === "jaw") return "⌒";
  if (id === "rib-cage") return "♢";
  if (id === "spine") return "⋮";
  if (id === "hip") return "◇";
  return "━";
}

function humanSkeletonSvg(): string {
  return `
    <svg viewBox="0 0 180 260" role="img" aria-label="Original simplified human skeleton model">
      <g fill="none" stroke="currentColor" stroke-width="7" stroke-linecap="round" stroke-linejoin="round">
        <ellipse cx="90" cy="28" rx="24" ry="25" />
        <path d="M73 43 Q90 57 107 43 M90 54 L90 143 M90 70 C55 67 55 113 90 116 C125 113 125 67 90 70 M70 142 Q90 160 110 142" />
        <path d="M71 72 L40 126 L25 175 M109 72 L140 126 L155 175 M78 151 L60 205 L51 254 M102 151 L120 205 L129 254" />
      </g>
    </svg>`;
}

function livingHumanDiagram(): string {
  return `
    <svg viewBox="0 0 260 150">
      <g class="human-body">
        <circle cx="130" cy="25" r="18" />
        <path d="M105 46 Q130 38 155 46 L168 96 L151 104 L148 142 H112 L109 104 L92 96Z" />
        <path d="M103 52 L72 102 M157 52 L188 102 M118 137 L104 149 M142 137 L156 149" />
      </g>
      <g class="living-bones" fill="none" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="130" cy="25" r="13" />
        <path d="M130 39 V103 M112 54 Q130 45 148 54 M112 60 Q130 84 148 60 M117 100 Q130 112 143 100 M112 53 L82 100 M148 53 L178 100 M120 108 L107 146 M140 108 L153 146" />
      </g>
      <path class="living-muscle" d="M107 58 Q99 72 93 86 L84 82 Q91 65 101 53Z" />
      <path class="growth-arrow" d="M25 122 H57 M47 113 L58 122 L47 131" />
    </svg>`;
}

function cardHumanModelDiagram(): string {
  return `
    <svg viewBox="0 0 260 150">
      <rect class="card-sheet" x="18" y="8" width="224" height="134" rx="10" />
      <g class="card-bones" fill="none" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="130" cy="28" r="16" />
        <path d="M130 45 V93 M111 54 Q130 45 149 54 M113 60 Q130 80 147 60 M116 92 Q130 104 144 92 M112 52 L81 101 M148 52 L179 101 M120 102 L105 137 M140 102 L155 137" />
      </g>
      <g class="paper-fasteners">
        <circle cx="130" cy="51" r="5" /><circle cx="112" cy="53" r="5" /><circle cx="148" cy="53" r="5" /><circle cx="120" cy="101" r="5" /><circle cx="140" cy="101" r="5" />
      </g>
      <path class="no-growth" d="M202 113 H228 M219 104 L229 113 L219 122" />
      <path class="no-growth-slash" d="M198 128 L233 97" />
    </svg>`;
}

function rabbitSkeletonDiagram(): string {
  return `
    <svg viewBox="0 0 260 150">
      <path class="rabbit-body" d="M50 93 C38 61 61 40 100 50 C126 22 183 28 206 62 C229 67 237 85 224 98 C206 116 165 113 137 108 C111 127 65 123 50 93Z" />
      <path class="rabbit-ear" d="M181 49 C178 21 187 4 197 7 C207 11 201 34 196 54 M164 44 C157 19 161 4 171 3 C181 5 180 28 179 48" />
      <circle class="rabbit-eye" cx="207" cy="69" r="4" />
      <g class="living-bones" fill="none" stroke-linecap="round" stroke-linejoin="round">
        <ellipse cx="194" cy="73" rx="18" ry="14" />
        <path d="M177 79 C151 72 126 73 101 82 M105 67 C89 60 72 62 65 76 C77 93 91 99 108 91 M105 80 L80 112 M132 79 L151 116 M159 76 L181 106" />
      </g>
    </svg>`;
}
